/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useEffect, useState } from 'react';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppShell as MantineAppShell,
  Avatar,
  Box,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { IconLogout, IconSun, IconMoon, IconDeviceDesktop } from '../components/icons';
import { useNavGroups } from './navigation';
import { useAppColors } from '../theme/useAppColors';
import { useCurrentUser, logout } from '../features/auth/hooks';
import styles from './style.module.css';

const NAVLINK_STYLES = { root: { borderRadius: '10px' } };

function initials(username: string | undefined): string {
  if (!username) return '';
  const alnum = username.replace(/[^A-Za-z0-9]/g, '');
  return alnum.slice(0, 2).toUpperCase();
}

export function AppShell() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const location = useLocation();
  const navGroups = useNavGroups();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);

  const [svgContent, setSvgContent] = useState<string | null>(null);
  const { data: user } = useCurrentUser();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  useEffect(() => {
    closeMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    fetch('public/assets/logo.svg')
      .then((response) => {
        if (!response.ok) throw new Error('Logo could not be fetched');
        return response.text();
      })
      .then((text) => {
        const dynamicSvg = text
          .replace(/<svg([^>]*)\bwidth="[^"]*"/g, '<svg$1')
          .replace(/<svg([^>]*)\bheight="[^"]*"/g, '<svg$1')
          .replace('<svg', '<svg viewBox="0 0 1600 674" style="width:100%; height:100%;"');
        setSvgContent(dynamicSvg);
      })
      .catch(() => {
      });
  }, []);

  return (
    <MantineAppShell
      header={{ height: { base: 56, sm: 0 } }}
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !mobileNavOpened } }}
      padding={0}
    >
      <MantineAppShell.Header hiddenFrom="sm" style={{ display: 'flex', alignItems: 'center', padding: '0 16px' }}>
        <Burger opened={mobileNavOpened} onClick={toggleMobileNav} size="sm" />
        <Group gap={8} ml="sm">
          <Box
            w={26}
            h={26}
            bg="entgraBlue.7"
            style={{
              borderRadius: 7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--mantine-color-white)',
              fontWeight: 600,
              fontSize: 12,
              flex: '0 0 auto',
            }}
          >
            {t('label_brandName').charAt(0)}
          </Box>
          <Text size="sm" fw={600}>
            {t('label_brandName')}
          </Text>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p={0} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box p="md" pb={40}>
            {svgContent ? (
              <div className={styles['black-svg-container']} dangerouslySetInnerHTML={{ __html: svgContent }} />
            ) : (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 11, paddingTop: 6 }}>
                <Box
                  w={34}
                  h={34}
                  bg="entgraBlue.7"
                  style={{
                    borderRadius: 9,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--mantine-color-white)',
                    fontWeight: 600,
                    fontSize: 15,
                    flex: '0 0 auto',
                  }}
                >
                  {t('label_brandName').charAt(0)}
                </Box>
                <Box>
                  <Text size="sm" fw={600} lh={1.2}>
                    {t('label_brandName')}
                  </Text>
                  <Text size="xs" c={colors.textMuted}>
                    {t('label_productName')}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>

          <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
            <Stack gap="md" px="md" pb="md">
              {navGroups.map((group) => (
                <Box key={group.titleKey}>
                  <Text
                    size="10.5px"
                    fw={700}
                    c={colors.textMuted}
                    tt="uppercase"
                    style={{ letterSpacing: '0.07em' }}
                    px={13}
                    pb={6}
                  >
                    {t(group.titleKey)}
                  </Text>
                  <Stack gap={3}>
                    {group.items.map((item) =>
                      item.enabled && item.to ? (
                        <NavLink
                          key={item.labelKey}
                          component={RouterNavLink}
                          to={item.to}
                          label={t(item.labelKey)}
                          leftSection={item.icon}
                          active={location.pathname === item.to}
                          variant="light"
                          styles={NAVLINK_STYLES}
                        />
                      ) : (
                        <NavLink
                          key={item.labelKey}
                          disabled
                          label={t(item.labelKey)}
                          leftSection={item.icon}
                          variant="light"
                          styles={NAVLINK_STYLES}
                        />
                      ),
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </ScrollArea>
        </Box>

        <Box p="md">
          <Divider mb="sm" />
          <Menu position="top-start" width={200} shadow="md">
            <Menu.Target>
              <UnstyledButton style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 8, width: '100%' }}>
                <Avatar radius="xl" size={30} color="gray">
                  {initials(user?.username)}
                </Avatar>
                <Box style={{ overflow: 'hidden' }}>
                  <Text size="12.5px" fw={600} truncate>
                    {user?.username ?? ''}
                  </Text>
                  <Text size="11px" c={colors.textMuted} truncate>
                    {t('label_productName')}
                  </Text>
                </Box>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t('label_appearance')}</Menu.Label>
              <Box px="sm" pb="xs">
                <SegmentedControl
                  fullWidth
                  size="xs"
                  withItemsBorders={false}
                  value={colorScheme}
                  onChange={(v) => setColorScheme(v as 'light' | 'dark' | 'auto')}
                  data={[
                    { value: 'light', label: <IconSun size={15} title={t('label_themeLight')} style={{ position: 'relative', top: '2px' }} /> },
                    { value: 'dark', label: <IconMoon size={15} title={t('label_themeDark')} style={{ position: 'relative', top: '2px' }} /> },
                    { value: 'auto', label: <IconDeviceDesktop size={15} title={t('label_themeSystem')} style={{ position: 'relative', top: '2px' }} /> },
                  ]}
                />
              </Box>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void logout()}>
                {t('label_logout')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main bg={colors.background} mih="100vh">
        <Outlet />
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
